import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import {
  ShieldAlert, Plus, X, Loader2, ChevronRight, Filter, UserX,
  CheckCircle2, RotateCcw, Ban, ArrowRight, Sparkles, Clock,
  Inbox, Eye, AlertTriangle, XCircle, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

// SU09 (valoriza-crm-comercial-unificado-auditoria-2026-10-13) · Inventario
// único de incidencias de calidad entre Admin, Mercado y Calidad. Los 8
// estados técnicos internos se proyectan sobre UNA línea de vida canónica
// compartida por las tres superficies: detectado → revisión → confirmado /
// falso positivo / resuelto (ver backend/routers/quality_incidents.py).
const FASE_ORDEN = ["detectado", "en_revision", "confirmado", "falso_positivo", "resuelto"];
const FASE_CFG = {
  detectado:      { label: "Detectado",      icon: Inbox,         cls: "bg-red-50 text-red-700 border-red-200",       dot: "bg-red-500" },
  en_revision:    { label: "En revisión",    icon: Eye,           cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  confirmado:     { label: "Confirmado",     icon: AlertTriangle, cls: "bg-blue-50 text-blue-700 border-blue-200",    dot: "bg-blue-500" },
  falso_positivo: { label: "Falso positivo", icon: XCircle,       cls: "bg-gray-50 text-gray-500 border-gray-200",    dot: "bg-gray-400" },
  resuelto:       { label: "Resuelto",       icon: CheckCircle2,  cls: "bg-green-50 text-green-700 border-green-200", dot: "bg-green-500" },
};
const ORIGEN_ORDEN = ["comercial", "tic", "operacion"];
const ORIGEN_CFG = {
  comercial: { label: "Comercial / Mercado" },
  tic:       { label: "TIC / Vínculos" },
  operacion: { label: "Operación / Admin" },
};

// R23 (criterios 2026-10-11) · Gestión de incidencias de calidad —
// categoría + enrutamiento por departamento (sugerencia, nunca persona
// inventada) + estados Abierta→En revisión→En resolución→Pendiente de
// validación→Resuelta (+Reabierta/Descartada). Ver backend/routers/quality_incidents.py.

const ESTADO_CFG = {
  abierta:                      { label: "Abierta",                   cls: "bg-red-100 text-red-700 border-red-200" },
  en_revision:                  { label: "En revisión",                cls: "bg-amber-100 text-amber-700 border-amber-200" },
  en_resolucion:                { label: "En resolución",              cls: "bg-blue-100 text-blue-700 border-blue-200" },
  pendiente_validacion:         { label: "Pendiente de validación",    cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  // N08 (roadmap-valoriza-jev-2026-10-11) · estado DISTINTO del anterior —
  // este lo pone un DETECTOR automático cuando ya no encuentra el problema
  // (nunca un humano claimando el fix). Nunca se autocierra a "resuelta".
  pendiente_validacion_resolucion: { label: "Detector: no encontrado (pendiente validar)", cls: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  resuelta:                     { label: "Resuelta",                   cls: "bg-green-100 text-green-700 border-green-200" },
  reabierta:                    { label: "Reabierta",                  cls: "bg-orange-100 text-orange-700 border-orange-200" },
  descartada_falso_positivo:    { label: "Descartada (falso positivo)", cls: "bg-gray-100 text-gray-500 border-gray-200" },
};
const CATEGORIA_LABEL = {
  datos_comerciales: "Datos comerciales", vinculos_tic: "Vínculos TIC",
  personas_tarifas: "Personas / tarifas", iniciativas: "Iniciativas",
  integracion: "Integración", otro: "Otro",
};
const NEXT_STATES = {
  abierta: ["en_revision", "descartada_falso_positivo"],
  en_revision: ["en_resolucion", "descartada_falso_positivo"],
  en_resolucion: ["pendiente_validacion", "descartada_falso_positivo"],
  pendiente_validacion: ["resuelta", "en_resolucion", "descartada_falso_positivo"],
  pendiente_validacion_resolucion: ["resuelta", "reabierta", "descartada_falso_positivo"],
  resuelta: ["reabierta"],
  reabierta: ["en_revision", "descartada_falso_positivo"],
  descartada_falso_positivo: [],
};
// N04 (roadmap-valoriza-jev-2026-10-11) · opciones EXACTAS pedidas por el
// usuario — nunca una quinta opción inventada.
const SIGUIENTE_ACCION_OPCIONES = ["vincular", "completar_informacion", "rechazar_candidato", "justificar_no_corresponde"];
const SIGUIENTE_ACCION_LABEL = {
  vincular: "Vincular", completar_informacion: "Completar información",
  rechazar_candidato: "Rechazar candidato", justificar_no_corresponde: "Justificar que no corresponde",
};

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ categoria: "datos_comerciales", motivo_deteccion: "", fuente: "", es_ejemplo: false });
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const suggestCategory = async () => {
    if (!form.motivo_deteccion.trim() || form.motivo_deteccion.trim().length < 3) {
      toast.error("Escribe primero el motivo de detección (mínimo 3 caracteres)");
      return;
    }
    setSuggesting(true);
    setSuggestion(null);
    try {
      const { data } = await axios.post(`${API}/quality-incidents/suggest-category`, {
        motivo_deteccion: form.motivo_deteccion, fuente: form.fuente,
      });
      if (data.suggested) setSuggestion(data);
      else toast.error("No se pudo obtener una sugerencia ahora — elige la categoría manualmente");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "No se pudo obtener la sugerencia");
    } finally { setSuggesting(false); }
  };

  const submit = async () => {
    if (!form.motivo_deteccion.trim() || !form.fuente.trim()) { toast.error("Motivo de detección y fuente son obligatorios"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/quality-incidents`, form);
      toast.success("Incidencia registrada");
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear la incidencia");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()} data-testid="quality-incident-create-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-semibold text-gray-900 font-['Outfit']">Nueva incidencia de calidad</h3>
          <button onClick={onClose} data-testid="quality-incident-create-close-btn"><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-gray-600 block">Categoría</label>
              <button type="button" onClick={suggestCategory} disabled={suggesting}
                data-testid="quality-incident-suggest-categoria-btn"
                className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 disabled:opacity-50">
                {suggesting ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                Sugerir con IA
              </button>
            </div>
            {suggestion && (
              <div className="mb-2 text-[11px] bg-indigo-50 border border-indigo-200 rounded-lg px-2.5 py-1.5 flex items-center justify-between" data-testid="quality-incident-category-suggestion">
                <span>Sugerencia: <strong>{CATEGORIA_LABEL[suggestion.suggested]}</strong> ({Math.round(suggestion.confidence * 100)}% confianza)</span>
                <button type="button" data-testid="quality-incident-suggest-categoria-apply-btn"
                  onClick={() => { setForm(f => ({ ...f, categoria: suggestion.suggested })); setSuggestion(null); }}
                  className="font-semibold text-indigo-700 hover:underline shrink-0 ml-2">Usar</button>
              </div>
            )}
            <select value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}
              data-testid="quality-incident-categoria-select"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
              {Object.entries(CATEGORIA_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Motivo de detección (lo que el detector sabe — no la explicación de negocio)</label>
            <textarea value={form.motivo_deteccion} onChange={e => setForm(f => ({ ...f, motivo_deteccion: e.target.value }))}
              data-testid="quality-incident-motivo-input" rows={3}
              placeholder="Ej: Dos expedientes con el mismo CIF pero municipios distintos sin vínculo confirmado"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Fuente (detector/sistema/persona que lo encontró)</label>
            <input value={form.fuente} onChange={e => setForm(f => ({ ...f, fuente: e.target.value }))}
              data-testid="quality-incident-fuente-input" placeholder="Ej: Sync Health, revisión manual, R08 normalización..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input type="checkbox" checked={form.es_ejemplo} onChange={e => setForm(f => ({ ...f, es_ejemplo: e.target.checked }))}
              data-testid="quality-incident-es-ejemplo-checkbox" />
            Es un ejemplo/PoC/postmortem de formación (se excluye de las métricas operativas)
          </label>
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500">Cancelar</button>
          <button onClick={submit} disabled={saving} data-testid="quality-incident-create-submit-btn"
            className="px-4 py-2 text-sm font-medium rounded-lg text-white disabled:opacity-60" style={{ background: "#007934" }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : "Registrar incidencia"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ incident, onClose, onUpdated, users }) {
  const [inc, setInc] = useState(incident);
  const [motivo, setMotivo] = useState("");
  const [evidencia, setEvidencia] = useState(inc.evidencia_cierre || "");
  const [propuesta, setPropuesta] = useState(inc.propuesta_resolucion || "");
  const [busy, setBusy] = useState(false);
  const [convertForm, setConvertForm] = useState({ responsable_email: "", fecha_objetivo: "" });

  const refresh = (data) => { setInc(data); onUpdated(data); };

  const transition = async (nuevo_estado) => {
    if (nuevo_estado === "descartada_falso_positivo" && !motivo.trim()) { toast.error("Indica el motivo del descarte"); return; }
    if (nuevo_estado === "resuelta" && !evidencia.trim() && !inc.evidencia_cierre) { toast.error("Indica la evidencia de cierre"); return; }
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/quality-incidents/${inc.id}/transition`, {
        nuevo_estado, motivo: motivo || undefined, evidencia_cierre: evidencia || undefined, propuesta_resolucion: propuesta || undefined,
      });
      refresh(data);
      setMotivo("");
      toast.success(`Estado actualizado a "${ESTADO_CFG[nuevo_estado]?.label}"`);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo actualizar"); }
    finally { setBusy(false); }
  };

  const assign = async (email) => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/quality-incidents/${inc.id}/asignar`, { responsable_email: email || null });
      refresh(data);
      toast.success(email ? `Asignada a ${email}` : "Vuelta a 'Sin responsable'");
    } catch (e) { toast.error("No se pudo asignar"); }
    finally { setBusy(false); }
  };

  const setSiguienteAccion = async (accion) => {
    let motivoAccion;
    if (["rechazar_candidato", "justificar_no_corresponde"].includes(accion)) {
      motivoAccion = window.prompt("Motivo (obligatorio):");
      if (!motivoAccion || !motivoAccion.trim()) return;
    }
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/quality-incidents/${inc.id}/siguiente-accion`, { siguiente_accion: accion, motivo: motivoAccion });
      refresh(data);
      toast.success("Siguiente acción actualizada");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo actualizar"); }
    finally { setBusy(false); }
  };

  const convertToReal = async () => {
    if (!convertForm.responsable_email || !convertForm.fecha_objetivo) { toast.error("Responsable y fecha objetivo son obligatorios"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/quality-incidents/${inc.id}/convert-to-real`, convertForm);
      toast.success("Convertida en incidencia real");
      onUpdated(data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo convertir"); }
    finally { setBusy(false); }
  };

  const cfg = ESTADO_CFG[inc.estado];
  const faseCfg = FASE_CFG[inc.fase] || FASE_CFG.detectado;
  const FaseIcon = faseCfg.icon;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="quality-incident-detail-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${faseCfg.cls}`} data-testid="quality-incident-detail-fase">
              <FaseIcon size={12} /> {faseCfg.label}
            </span>
            {inc.origen_superficie && (
              <span className="text-xs text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">{ORIGEN_CFG[inc.origen_superficie]?.label || inc.origen_superficie}</span>
            )}
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cfg.cls}`} data-testid="quality-incident-detail-estado" title="Estado técnico interno">{cfg.label}</span>
            <span className="text-xs text-gray-400">{CATEGORIA_LABEL[inc.categoria] || inc.categoria}</span>
            {inc.es_ejemplo && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Ejemplo (excluido de métricas)</span>}
          </div>
          <button onClick={onClose} data-testid="quality-incident-detail-close-btn"><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">Motivo de detección</p>
            <p className="text-sm text-gray-800">{inc.motivo_deteccion}</p>
            <p className="text-[11px] text-gray-400 mt-1">Fuente: {inc.fuente} · Primera detección: {new Date(inc.primera_deteccion).toLocaleString("es-ES")} · Última: {new Date(inc.ultima_deteccion).toLocaleString("es-ES")}</p>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">Responsable</p>
            {inc.needs_assignment && (
              <p className="text-[11px] text-amber-600 mb-1.5 flex items-center gap-1"><UserX size={12} /> Sin responsable — sugerido: {(inc.responsable_sugerido || []).join(", ") || "sin sugerencia"}</p>
            )}
            <select value={inc.responsable_email || ""} onChange={e => assign(e.target.value)} disabled={busy}
              data-testid="quality-incident-asignar-select"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none bg-white">
              <option value="">Sin responsable</option>
              {users.map(u => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
            </select>
          </div>

          {inc.categoria === "vinculos_tic" && (
            <div data-testid="quality-incident-siguiente-accion-block">
              <p className="text-xs font-semibold text-gray-500 mb-1">Siguiente acción (N04)</p>
              {inc.siguiente_accion_sugerida && !inc.siguiente_accion && (
                <p className="text-[11px] text-sky-600 mb-1.5">Sugerida: {SIGUIENTE_ACCION_LABEL[inc.siguiente_accion_sugerida]}</p>
              )}
              {inc.siguiente_accion ? (
                <p className="text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                  {SIGUIENTE_ACCION_LABEL[inc.siguiente_accion]}
                  {inc.siguiente_accion_motivo && <span className="text-gray-500"> — {inc.siguiente_accion_motivo}</span>}
                  <span className="text-[10px] text-gray-400 block mt-0.5">por {inc.siguiente_accion_set_by}</span>
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {SIGUIENTE_ACCION_OPCIONES.map(op => (
                    <button key={op} onClick={() => setSiguienteAccion(op)} disabled={busy}
                      data-testid={`quality-incident-siguiente-accion-${op}-btn`}
                      className="px-2.5 py-1 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                      {SIGUIENTE_ACCION_LABEL[op]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!inc.es_ejemplo && (
            <>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">Propuesta de resolución</p>
                <textarea value={propuesta} onChange={e => setPropuesta(e.target.value)} rows={2}
                  data-testid="quality-incident-propuesta-input"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
              </div>
              {(inc.estado === "pendiente_validacion" || inc.evidencia_cierre) && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">Evidencia de cierre (relación confirmada / documento corregido / comparación de registros / resultado de prueba)</p>
                  <textarea value={evidencia} onChange={e => setEvidencia(e.target.value)} rows={2}
                    data-testid="quality-incident-evidencia-input"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                </div>
              )}
              {NEXT_STATES[inc.estado]?.includes("descartada_falso_positivo") && (
                <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo si vas a descartar como falso positivo"
                  data-testid="quality-incident-motivo-descarte-input"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                {(NEXT_STATES[inc.estado] || []).map(st => (
                  <button key={st} onClick={() => transition(st)} disabled={busy} data-testid={`quality-incident-transition-${st}-btn`}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1 disabled:opacity-60 ${st === "descartada_falso_positivo" ? "border border-gray-200 text-gray-500 hover:bg-gray-50" : "text-white"}`}
                    style={st !== "descartada_falso_positivo" ? { background: "#007934" } : {}}>
                    {st === "descartada_falso_positivo" ? <Ban size={12} /> : st === "resuelta" ? <CheckCircle2 size={12} /> : st === "reabierta" ? <RotateCcw size={12} /> : <ArrowRight size={12} />}
                    {ESTADO_CFG[st]?.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {inc.es_ejemplo && (
            <div className="bg-purple-50 border border-purple-100 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-purple-700 flex items-center gap-1"><Sparkles size={12} /> Convertir a incidencia real</p>
              <select value={convertForm.responsable_email} onChange={e => setConvertForm(f => ({ ...f, responsable_email: e.target.value }))}
                data-testid="quality-incident-convert-responsable-select"
                className="w-full border border-purple-200 rounded-lg px-3 py-2 text-xs focus:outline-none bg-white">
                <option value="">Elegir responsable...</option>
                {users.map(u => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
              </select>
              <input type="date" value={convertForm.fecha_objetivo} onChange={e => setConvertForm(f => ({ ...f, fecha_objetivo: e.target.value }))}
                data-testid="quality-incident-convert-fecha-input"
                className="w-full border border-purple-200 rounded-lg px-3 py-2 text-xs focus:outline-none" />
              <button onClick={convertToReal} disabled={busy} data-testid="quality-incident-convert-submit-btn"
                className="w-full px-3 py-2 text-xs font-medium rounded-lg bg-purple-600 text-white disabled:opacity-60">Convertir a trabajo real</button>
            </div>
          )}

          <details>
            <summary className="text-[11px] text-gray-400 cursor-pointer">Historial ({inc.historial?.length || 0})</summary>
            <div className="mt-1.5 space-y-1">
              {(inc.historial || []).slice().reverse().map((h, i) => (
                <p key={i} className="text-[11px] text-gray-500">
                  {ESTADO_CFG[h.estado_anterior]?.label || "—"} → <strong>{ESTADO_CFG[h.estado_nuevo]?.label}</strong> · {h.usuario} · {new Date(h.fecha).toLocaleString("es-ES")}{h.motivo ? ` · "${h.motivo}"` : ""}
                </p>
              ))}
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

export default function QualityIncidents() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [inventario, setInventario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [users, setUsers] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    categoria: "", estado: "", fase: "", origen: "", sin_responsable: false, soloStale: false,
  });

  // SU09 · un enlace desde Mercado/Admin como "/calidad?origen=comercial&fase=detectado"
  // abre el inventario ya filtrado por esa superficie/fase.
  useEffect(() => {
    const o = searchParams.get("origen");
    const f = searchParams.get("fase");
    if (o || f) setFilters(prev => ({ ...prev, origen: o || prev.origen, fase: f || prev.fase }));
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.categoria) params.categoria = filters.categoria;
      if (filters.estado) params.estado = filters.estado;
      if (filters.fase) params.fase = filters.fase;
      if (filters.origen) params.origen = filters.origen;
      if (filters.sin_responsable) params.sin_responsable = true;
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/quality-incidents`, { params }),
        axios.get(`${API}/quality-incidents/summary/metrics`),
        axios.get(`${API}/quality-incidents/inventario`),
      ]);
      setItems(r1.data || []);
      setSummary(r2.data);
      setInventario(r3.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/horas/users`).then(r => setUsers(r.data || [])).catch(() => {}); }, []);

  // N12 (roadmap-valoriza-jev-2026-10-11, Ola 4) · "Mi trabajo y los
  // enlaces de retorno conservan contexto" — un enlace desde Mi trabajo
  // como "/calidad?open={id}" abre directamente esa incidencia, en vez de
  // dejar al usuario buscarla otra vez en la lista completa.
  useEffect(() => {
    if (!items.length) return;
    const openId = new URLSearchParams(window.location.search).get("open");
    if (openId) {
      const found = items.find(i => i.id === openId);
      if (found) setSelected(found);
    }
  }, [items]);

  // N08 (roadmap-valoriza-jev-2026-10-11) · conecta los detectores
  // automáticos existentes (ofertas sin vínculo, licitaciones en limbo,
  // duplicados comerciales, PoCs duplicados) con incidencias reales —
  // identidad estable, nunca duplica en ejecuciones repetidas.
  const syncDetectors = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/quality-incidents/sync-detectors`);
      const s = r.data.summary;
      const total = Object.values(s).reduce((acc, v) => acc + v.detectadas, 0);
      const pendientes = Object.values(s).reduce((acc, v) => acc + v.pendientes_validacion_resolucion, 0);
      toast.success(`Detectores sincronizados: ${total} hallazgos activos${pendientes ? `, ${pendientes} ya no se detectan (pendiente validar)` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo sincronizar");
    } finally { setSyncing(false); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="quality-incidents-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ShieldAlert size={22} className="text-[#007934]" />
          <h1 className="text-xl font-bold text-gray-900 font-['Outfit']">Incidencias de Calidad</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={syncDetectors} disabled={syncing} data-testid="quality-incidents-sync-detectors-btn"
            className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-[#007934] border border-[#007934]/30 rounded-lg hover:bg-[#007934]/5 disabled:opacity-50">
            {syncing ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Sincronizar detectores
          </button>
          <button onClick={() => setShowCreate(true)} data-testid="quality-incident-new-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ background: "#007934" }}>
            <Plus size={14} /> Nueva incidencia
          </button>
        </div>
      </div>

      {/* SU09 · Inventario único: una sola línea de vida compartida por
          Admin, Mercado y Calidad. El desglose por superficie deja ver de
          dónde viene cada hallazgo sin duplicar el conteo. */}
      {inventario && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-4" data-testid="quality-incidents-inventario">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Layers size={15} className="text-[#007934]" />
              <h2 className="text-sm font-bold text-gray-800">Inventario único de calidad</h2>
              <span className="text-[11px] text-gray-400">· Admin · Mercado · Calidad</span>
            </div>
            <span className="text-[11px] text-gray-400" data-testid="quality-incidents-ultima-sync">
              {inventario.ultima_sync
                ? `Detectores sincronizados: ${new Date(inventario.ultima_sync).toLocaleString("es-ES")}`
                : "Detectores aún no sincronizados — pulsa «Sincronizar detectores»"}
            </span>
          </div>

          {/* Línea de vida canónica: detectado → revisión → confirmado / falso positivo / resuelto */}
          <div className="flex items-stretch gap-1.5 flex-wrap" data-testid="quality-incidents-fase-strip">
            {FASE_ORDEN.map((f, idx) => {
              const cfg = FASE_CFG[f];
              const Icon = cfg.icon;
              const count = inventario.fases_global?.[f] ?? 0;
              const active = filters.fase === f;
              return (
                <React.Fragment key={f}>
                  <button
                    type="button"
                    onClick={() => setFilters(prev => ({ ...prev, fase: active ? "" : f }))}
                    data-testid={`quality-incidents-fase-${f}`}
                    className={`flex-1 min-w-[108px] text-left border rounded-lg px-3 py-2 transition ${cfg.cls} ${active ? "ring-2 ring-offset-1 ring-[#007934]" : "hover:brightness-95"}`}
                  >
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide">
                      <Icon size={12} /> {cfg.label}
                    </div>
                    <div className="text-xl font-bold mt-0.5">{count}</div>
                  </button>
                  {idx < FASE_ORDEN.length - 1 && (
                    <div className="flex items-center text-gray-300 self-center">
                      <ArrowRight size={13} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Superficie de origen: Mercado ve lo comercial, Admin lo de
              operación, Calidad el total — todo del MISMO inventario. */}
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="quality-incidents-origen-tabs">
            <span className="text-[11px] text-gray-500 font-semibold mr-1">Superficie:</span>
            <button
              type="button"
              onClick={() => setFilters(prev => ({ ...prev, origen: "" }))}
              data-testid="quality-incidents-origen-todas"
              className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold transition ${filters.origen === "" ? "bg-[#007934] text-white border-[#007934]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
            >
              Todas <b className="font-mono">{inventario.total_real}</b>
            </button>
            {ORIGEN_ORDEN.map(o => {
              const data = inventario.por_origen?.[o] || {};
              const active = filters.origen === o;
              return (
                <button
                  key={o}
                  type="button"
                  onClick={() => setFilters(prev => ({ ...prev, origen: active ? "" : o }))}
                  data-testid={`quality-incidents-origen-${o}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold transition ${active ? "bg-slate-800 text-white border-slate-800" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
                  title={`${data.abiertas || 0} abiertas · ${data.sin_responsable || 0} sin responsable`}
                >
                  {ORIGEN_CFG[o].label} <b className="font-mono">{data.total || 0}</b>
                  {(data.abiertas || 0) > 0 && <span className="ml-1 opacity-70">({data.abiertas} abiertas)</span>}
                </button>
              );
            })}
            <span className="ml-auto text-[11px] text-gray-400">
              <b className="text-amber-600">{inventario.sin_responsable}</b> abiertas sin responsable
              {inventario.ejemplos_excluidos > 0 && <> · <b className="text-purple-500">{inventario.ejemplos_excluidos}</b> ejemplos excluidos</>}
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={14} className="text-gray-400" />
        <select value={filters.categoria} onChange={e => setFilters(f => ({ ...f, categoria: e.target.value }))}
          data-testid="quality-incidents-filter-categoria"
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none bg-white">
          <option value="">Todas las categorías</option>
          {Object.entries(CATEGORIA_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={filters.estado} onChange={e => setFilters(f => ({ ...f, estado: e.target.value }))}
          data-testid="quality-incidents-filter-estado"
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none bg-white">
          <option value="">Todos los estados (detalle técnico)</option>
          {Object.entries(ESTADO_CFG).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={filters.sin_responsable} onChange={e => setFilters(f => ({ ...f, sin_responsable: e.target.checked }))} data-testid="quality-incidents-filter-sin-responsable" />
          Sin responsable
        </label>
        <label className="flex items-center gap-1.5 text-xs text-orange-700">
          <input type="checkbox" checked={filters.soloStale} onChange={e => setFilters(f => ({ ...f, soloStale: e.target.checked }))} data-testid="quality-incidents-filter-stale" />
          Solo sin movimiento
        </label>
        {(filters.fase || filters.origen) && (
          <button type="button" onClick={() => setFilters(f => ({ ...f, fase: "", origen: "" }))}
            data-testid="quality-incidents-clear-fase-origen"
            className="text-[11px] text-gray-500 hover:text-red-600">✕ limpiar fase/superficie</button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" /></div>
      ) : items.filter(it => !filters.soloStale || it.is_stale).length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm" data-testid="quality-incidents-empty-state">Sin incidencias registradas con estos filtros.</div>
      ) : (
        <div className="space-y-2" data-testid="quality-incidents-list">
          {items.filter(it => !filters.soloStale || it.is_stale).map(it => {
            const cfg = ESTADO_CFG[it.estado];
            const faseCfg = FASE_CFG[it.fase] || FASE_CFG.detectado;
            const FaseIcon = faseCfg.icon;
            return (
              <div key={it.id} onClick={() => setSelected(it)} data-testid={`quality-incident-card-${it.id}`}
                className="bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-between gap-3 cursor-pointer hover:border-[#007934]/40 transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {/* SU09 · fase canónica (lo primero que se lee) + origen */}
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${faseCfg.cls}`} data-testid={`quality-incident-fase-${it.id}`}>
                      <FaseIcon size={11} /> {faseCfg.label}
                    </span>
                    {it.origen_superficie && (
                      <span className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-full" data-testid={`quality-incident-origen-${it.id}`}>
                        {ORIGEN_CFG[it.origen_superficie]?.label || it.origen_superficie}
                      </span>
                    )}
                    <span className="text-[11px] text-gray-300" title={`Estado técnico: ${cfg.label}`}>· {cfg.label}</span>
                    <span className="text-[11px] text-gray-400">{CATEGORIA_LABEL[it.categoria] || it.categoria}</span>
                    {it.detector_key && (
                      <span className="text-[11px] font-mono text-gray-400 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded" title={`Detectado automáticamente ${it.occurrence_count || 1} veces`}>
                        {it.fuente}{it.occurrence_count > 1 ? ` ×${it.occurrence_count}` : ""}
                      </span>
                    )}
                    {it.es_ejemplo && <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Ejemplo</span>}
                    {it.needs_assignment && <span className="text-[11px] text-amber-600 flex items-center gap-0.5"><UserX size={10} /> Sin responsable</span>}
                    {it.is_stale && (
                      <span className="text-[11px] font-bold text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded flex items-center gap-0.5" data-testid={`quality-incident-stale-badge-${it.id}`}>
                        <Clock size={10} /> {it.dias_sin_movimiento} días sin movimiento
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-800 truncate">{it.motivo_deteccion}</p>
                </div>
                <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {selected && (
        <DetailModal incident={selected} users={users}
          onClose={() => { setSelected(null); load(); }}
          onUpdated={(data) => { setItems(prev => prev.map(i => i.id === data.id ? data : i)); setSelected(data); }}
        />
      )}
    </div>
  );
}
